import React, { useEffect, useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { PublicNav } from '../components/PublicNav';
import { MessageGregBubble } from '../components/MessageGregBubble';
import { DemoLeadSection } from '../components/DemoLeadSection';
import { CompareCrossLink } from '../components/CompareCrossLink';
import { BeTheFirstCallout } from '../components/BeTheFirstCallout';
import { RegionMap } from '../components/RegionMap';
import { findRegion, REGIONS } from '../data/regions';
import { TOP_STATES } from '../data/states';
import { Globe2, Calendar, CreditCard, MessageSquare, Users, ShieldCheck, ArrowRight, ChevronRight } from 'lucide-react';

// ----------------------------------------------------------------------
// RegionLanding — /dog-training-software/canada + /worldwide
// ----------------------------------------------------------------------
// One template, two pages. Reads the region config from /data/regions.js
// (Canada or Worldwide) and renders a full local-SEO landing page with
// per-region title/desc/canonical + Service, FAQPage, and Breadcrumb
// JSON-LD. Kept separate from StateLanding so US-only copy stays clean.
// ----------------------------------------------------------------------

const FEATURES = [
  { icon: Calendar,       title: 'Online booking that never double-books',                desc: 'Fill boarding runs, class rosters, and board-and-train intakes without overlaps.' },
  { icon: CreditCard,     title: 'Built-in POS + monthly memberships',                    desc: 'Sell packages, run recurring VIP billing, and take card payments at the front desk.' },
  { icon: MessageSquare,  title: 'Direct client messaging & live trainer video',         desc: 'Send daily updates, images, and short training clips to owners from any device.' },
  { icon: Users,          title: 'One universal login for owners, trainers & staff',     desc: 'Multi-tier access for Admins, Trainers, Front Desk, and Clients under one system.' },
];

const setMeta = (name, content, attr = 'name') => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute('content', content);
};
const setCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement('link'); el.setAttribute('rel', 'canonical'); document.head.appendChild(el); }
  el.setAttribute('href', href);
};
const upsertJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement('script'); el.type = 'application/ld+json'; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
};
const cleanupJsonLd = (id) => { const el = document.getElementById(id); if (el) el.remove(); };

// Optional prop `region` lets a parent route pass the config in directly;
// otherwise we resolve from the URL param. Keeps App.js flexible.
export default function RegionLanding({ region: forcedRegion }) {
  const { region: slug } = useParams();
  const region = forcedRegion || findRegion(slug);

  const canonical = region ? `https://k9os.app/dog-training-software/${region.slug}` : 'https://k9os.app/dog-training-software';

  useEffect(() => {
    if (!region) return;
    const prevTitle = document.title;
    document.title = region.seoTitle;
    setMeta('description', region.seoDesc);
    setMeta('keywords', region.keywords.join(', '));
    setCanonical(canonical);
    setMeta('og:title', region.seoTitle, 'property');
    setMeta('og:description', region.seoDesc, 'property');
    setMeta('og:url', canonical, 'property');
    setMeta('twitter:title', region.seoTitle);
    setMeta('twitter:description', region.seoDesc);

    upsertJsonLd('k9os-region-jsonld', {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Service',
          '@id': `${canonical}#service`,
          name: region.headline,
          serviceType: 'Dog Training Software',
          provider: { '@id': 'https://k9os.app/#organization' },
          areaServed:
            region.slug === 'canada'
              ? { '@type': 'Country', name: 'Canada' }
              : { '@type': 'Place', name: 'Worldwide (International)' },
          url: canonical,
          description: region.seoDesc,
        },
        {
          '@type': 'FAQPage',
          '@id': `${canonical}#faq`,
          mainEntity: region.faq.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://k9os.app/' },
            { '@type': 'ListItem', position: 2, name: 'Dog Training Software by Region', item: 'https://k9os.app/dog-training-software' },
            { '@type': 'ListItem', position: 3, name: region.title, item: canonical },
          ],
        },
      ],
    });

    return () => {
      document.title = prevTitle;
      cleanupJsonLd('k9os-region-jsonld');
    };
  }, [region, canonical]);

  const otherRegionSlug = useMemo(() => (region?.slug === 'canada' ? 'worldwide' : 'canada'), [region]);

  if (!region) {
    return <Navigate to='/dog-training-software' replace />;
  }

  return (
    <div className='min-h-screen bg-white dark:bg-slate-950' data-testid={`region-landing-${region.slug}`}>
      <PublicNav />

      {/* Breadcrumb */}
      <div className='border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900'>
        <nav aria-label='Breadcrumb' className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-xs text-slate-600 dark:text-slate-400 flex items-center gap-1'>
          <Link to='/' className='hover:text-[#1B3A87] dark:hover:text-[#93C5FD]'>Home</Link>
          <ChevronRight className='w-3 h-3 opacity-60' />
          <Link to='/dog-training-software' className='hover:text-[#1B3A87] dark:hover:text-[#93C5FD]'>Dog Training Software by Region</Link>
          <ChevronRight className='w-3 h-3 opacity-60' />
          <span className='text-slate-900 dark:text-slate-100 font-medium'>{region.title}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className='relative overflow-hidden bg-gradient-to-b from-white to-slate-50 dark:from-slate-950 dark:to-slate-900'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20'>
          <div className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1B3A87]/10 text-[#1B3A87] dark:bg-[#93C5FD]/10 dark:text-[#93C5FD] text-xs font-semibold uppercase tracking-[0.18em]'>
            <Globe2 className='w-3.5 h-3.5' /> {region.eyebrow}
          </div>
          <h1
            className='mt-4 font-display text-3xl sm:text-4xl md:text-5xl font-bold text-[#0B1220] dark:text-slate-50 leading-tight max-w-4xl'
            data-testid='region-h1'
          >
            {region.headline} <span className='text-[#C8102E]'>&mdash;</span> {region.subheadline}
          </h1>
          <p className='mt-5 text-slate-600 dark:text-slate-300 text-base md:text-lg leading-relaxed max-w-3xl'>
            {region.intro}
          </p>
          <div className='mt-7 flex flex-wrap items-center gap-3'>
            <Link
              to='/contact'
              className='inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C8102E] hover:bg-[#A50D26] text-white text-sm font-bold shadow-lg transition-colors'
              data-testid='region-cta-demo'
            >
              Book a Free Demo <ArrowRight className='w-4 h-4' />
            </Link>
            <Link
              to='/'
              className='inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-100 hover:border-[#1B3A87] dark:hover:border-[#93C5FD] text-sm font-semibold'
              data-testid='region-cta-tour'
            >
              See the full K9os tour
            </Link>
          </div>

          {/* First-mover promo — no adoption claims, just an invitation to
              claim K9os in this region before another dog trainer does. */}
          <BeTheFirstCallout
            className='mt-8'
            scopeLabel='region'
            areaSuffix={`in ${region.title}`}
            ctaLabel='Claim your area first'
            testid='region-be-the-first'
          />
        </div>
      </section>

      {/* Regional map — Canada + UK get their own zoomed-in map that
          echoes the main "Trainers Everywhere" globe on the home page.
          Worldwide keeps its current layout (the home-page LeadMap is
          already a worldwide view). */}
      {region.mapCities && region.mapCenter && (
        <RegionMap
          countryName={region.slug === 'canada' ? 'Canada' : region.slug === 'uk' ? 'United Kingdom' : region.title}
          cities={region.mapCities}
          eyebrow={`K9os ready in ${region.title}`}
          heading={`Where K9os is ready to launch across ${region.areaLabel || region.title}`}
          subheading={`Every glowing city on the map is a top dog-training market ready to onboard onto K9os. Tap a ${region.slug === 'canada' ? 'province' : 'region'} to jump to its section below — and be the FIRST dog trainer or facility there to run on K9os.`}
          projectionConfig={{
            center: region.mapCenter,
            scale: region.mapScale || 800,
            ...(region.mapTranslate ? { translate: region.mapTranslate } : {}),
          }}
          aspect={region.mapAspect}
          subdivisionsGeo={region.subdivisionsGeo}
          subdivisionNameKey={region.subdivisionNameKey}
        />
      )}

      {/* Features */}
      <section className='py-14 md:py-16 bg-white dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C8102E]/10 text-[#C8102E] dark:bg-[#F87171]/10 dark:text-[#F87171] text-xs font-semibold uppercase tracking-[0.18em]'>
            <ShieldCheck className='w-3.5 h-3.5' /> Built for {region.title} Dog Trainers
          </div>
          <h2 className='mt-4 font-display text-2xl md:text-3xl font-bold text-[#0B1220] dark:text-slate-50 leading-tight max-w-3xl'>
            Everything a {region.title.toLowerCase() === 'worldwide' ? 'modern' : region.title} dog training business runs on &mdash; in one login
          </h2>
          <div className='mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5' data-testid='region-features'>
            {FEATURES.map((f, i) => (
              <div key={i} className='rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all'>
                <div className='w-11 h-11 rounded-xl bg-[#1B3A87]/10 dark:bg-[#93C5FD]/10 text-[#1B3A87] dark:text-[#93C5FD] flex items-center justify-center'>
                  <f.icon className='w-5 h-5' />
                </div>
                <h3 className='mt-4 font-display text-lg font-bold text-[#0B1220] dark:text-slate-50 leading-snug'>{f.title}</h3>
                <p className='mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed'>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regions / Provinces served */}
      <section className='py-14 md:py-16 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950 border-t border-slate-200 dark:border-slate-800'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'>
          <h2 className='font-display text-2xl md:text-3xl font-bold text-[#0B1220] dark:text-slate-50 leading-tight'>
            Ready for dog trainers across {region.areaLabel}
          </h2>
          <p className='mt-3 text-slate-600 dark:text-slate-300 max-w-3xl leading-relaxed'>{region.areaBlurb}</p>

          {/* Provinces / countries */}
          <div className='mt-6'>
            <div className='text-[11px] uppercase tracking-[0.18em] font-bold text-[#C8102E] mb-3'>
              {region.slug === 'canada' ? 'Provinces & Territories' : 'Countries & Markets'}
            </div>
            <ul className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3' data-testid='region-regions'>
              {region.regions.map((r) => {
                const rid = 'region-' + r.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
                return (
                  <li
                    key={r}
                    id={rid}
                    className='px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-center text-sm font-semibold text-[#1B3A87] dark:text-[#93C5FD] scroll-mt-24 target:ring-2 target:ring-[#C8102E]'
                    data-testid={`region-chip-${rid}`}
                  >
                    {r}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Cities */}
          <div className='mt-8'>
            <div className='text-[11px] uppercase tracking-[0.18em] font-bold text-[#C8102E] mb-3'>Major Metros</div>
            <ul className='flex flex-wrap gap-2' data-testid='region-cities'>
              {region.cities.map((c) => (
                <li key={c} className='px-3 py-1 rounded-full text-xs font-semibold bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-[#1B3A87] dark:text-[#93C5FD]'>
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className='py-14 md:py-16 bg-white dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800'>
        <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1B3A87]/10 text-[#1B3A87] dark:bg-[#93C5FD]/10 dark:text-[#93C5FD] text-xs font-semibold uppercase tracking-[0.18em]'>
            FAQ &middot; {region.title}
          </div>
          <h2 className='mt-4 font-display text-2xl md:text-3xl font-bold text-[#0B1220] dark:text-slate-50 leading-tight'>
            Dog training software questions from {region.slug === 'canada' ? 'Canadian' : 'international'} trainers
          </h2>
          <div className='mt-8 space-y-3' data-testid='region-faq'>
            {region.faq.map((f, i) => (
              <details
                key={i}
                className='group rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 open:shadow-md'
                {...(i === 0 ? { open: true } : {})}
              >
                <summary className='cursor-pointer list-none px-5 py-4 flex items-center justify-between gap-4'>
                  <h3 className='text-base md:text-lg font-semibold text-[#0B1220] dark:text-slate-50 leading-snug'>{f.q}</h3>
                  <span className='shrink-0 w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 text-[#1B3A87] dark:text-[#93C5FD] flex items-center justify-center transition-transform group-open:rotate-180'>
                    <ChevronRight className='w-4 h-4 rotate-90' />
                  </span>
                </summary>
                <p className='px-5 pb-5 text-sm md:text-[15px] leading-relaxed text-slate-600 dark:text-slate-300'>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + cross-links */}
      <section className='py-14 md:py-16 bg-gradient-to-r from-[#1B3A87]/5 via-white to-[#C8102E]/5 dark:from-[#1B3A87]/15 dark:via-slate-900 dark:to-[#C8102E]/15 border-t border-slate-200 dark:border-slate-800'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-8 items-center'>
          <div>
            <h2 className='font-display text-2xl md:text-3xl font-bold text-[#0B1220] dark:text-slate-50 leading-tight'>
              Ready to run your {region.title.toLowerCase() === 'worldwide' ? 'international' : region.title} dog training business on one system?
            </h2>
            <p className='mt-3 text-slate-600 dark:text-slate-300 leading-relaxed'>
              Book a free walkthrough. We&rsquo;ll tailor the demo to your operation &mdash; whether you run day training,
              board-and-train, group classes, or a full boarding facility.
            </p>
            <Link
              to='/contact'
              className='mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C8102E] hover:bg-[#A50D26] text-white text-sm font-bold shadow-lg'
              data-testid='region-cta-bottom'
            >
              Book a Free Demo <ArrowRight className='w-4 h-4' />
            </Link>
          </div>
          <div>
            <div className='text-xs uppercase tracking-[0.18em] font-bold text-[#1B3A87] dark:text-[#93C5FD]'>Also serving</div>
            <h3 className='mt-2 font-display text-lg font-bold text-[#0B1220] dark:text-slate-50'>Top dog-training markets</h3>
            <ul className='mt-3 flex flex-wrap gap-2' data-testid='region-cross-links'>
              <li>
                <Link
                  to={`/dog-training-software/${otherRegionSlug}`}
                  className='px-3 py-1 inline-block rounded-full text-xs font-semibold bg-[#1B3A87]/10 text-[#1B3A87] dark:bg-[#93C5FD]/15 dark:text-[#93C5FD] border border-[#1B3A87]/20 dark:border-[#93C5FD]/25 hover:border-[#C8102E]'
                >
                  {REGIONS[otherRegionSlug].title}
                </Link>
              </li>
              {TOP_STATES.slice(0, 12).map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/dog-training-software/${s.slug}`}
                    className='px-3 py-1 inline-block rounded-full text-xs font-semibold bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-[#1B3A87] dark:text-[#93C5FD] hover:border-[#C8102E]'
                  >
                    {s.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to='/dog-training-software'
                  className='px-3 py-1 inline-block rounded-full text-xs font-semibold bg-[#1B3A87] text-white hover:bg-[#16306F]'
                >
                  View all 50 states &rarr;
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* SEO cross-link to /compare — same pattern used on every state
          page. Positioned above the DemoLeadSection. */}
      <CompareCrossLink regionName={region.title} />

      {/* Full lead-capture form — one submit per region page.
          Prefills the region name into the location field and tags the
          notes so Greg can trace the source page. Region-aware heading
          reads naturally for Canada / UK / Worldwide. */}
      <DemoLeadSection
        regionName={region.title}
        regionSlug={region.slug}
        headingOverride={
          region.slug === 'worldwide'
            ? 'Ready to grow your dog-training business — anywhere in the world?'
            : region.slug === 'uk'
              ? 'Ready to grow your UK dog-training business?'
              : `Ready to grow your ${region.title} dog-training business?`
        }
      />

      <MessageGregBubble />
    </div>
  );
}
